import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCreateBlockNote, SuggestionMenuController } from '@blocknote/react';
import { BlockNoteView } from '@blocknote/mantine';
import { multiColumnDropCursor } from '@blocknote/xl-multi-column';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import useTheme from '../../lib/useTheme';
import useCollab from './useCollab';
import IconPicker from './IconPicker';
import CoverPicker from './CoverPicker';
import PageOptionsMenu from './PageOptionsMenu';
import CommentsPanel from './CommentsPanel';
import VersionHistory from './VersionHistory';
import PresentationView from './PresentationView';
import AnalyticsPanel from './AnalyticsPanel';
import BlockSideMenu from './BlockSideMenu';
import ShareModal from '../share/ShareModal';
import { schema } from './blocks/schema';
import { getRootSlashMenuItems } from './blocks/slashMenuItems';
import { createInternalLinkClickHandler } from './internalLinkNav';
import '@blocknote/mantine/style.css';
import './page-editor.css';

const TITLE_DEBOUNCE_MS = 600;
const COLORS = ['#2383e2', '#e0561d', '#0f9d58', '#a142f4', '#d93025', '#f4b400', '#1abc9c', '#9b59b6'];
function colorFor(id) {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return COLORS[h % COLORS.length];
}

// 현재 접속자(awareness) 표시
function Presence({ awareness }) {
  const [users, setUsers] = useState([]);
  useEffect(() => {
    if (!awareness) return undefined;
    const update = () => {
      const seen = new Map();
      awareness.getStates().forEach((state, clientId) => {
        const u = state && state.user;
        if (u && u.name) seen.set(clientId, u);
      });
      setUsers(Array.from(seen.entries()).map(([clientId, u]) => ({ clientId, ...u })));
    };
    update();
    awareness.on('change', update);
    return () => awareness.off('change', update);
  }, [awareness]);

  if (users.length <= 1) {
    return <span className="pe-live"><span className="pe-live-dot" /> 나만 보는 중</span>;
  }
  return (
    <span className="pe-live">
      <span className="pe-live-dot" /> {users.length}명 편집 중
      <span style={{ display: 'inline-flex', marginLeft: 6 }}>
        {users.slice(0, 5).map((u) => (
          <span
            key={u.clientId}
            title={u.name}
            style={{
              width: 22, height: 22, borderRadius: '50%', background: u.color || '#888',
              color: '#fff', fontSize: 10, fontWeight: 700, display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', border: '2px solid #fff', marginLeft: -6,
            }}
          >
            {(u.name || '?').slice(0, 1)}
          </span>
        ))}
      </span>
    </span>
  );
}

export default function PageEditor() {
  const { pageId } = useParams();
  const { user, membership } = useAuth();

  const [page, setPage] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [title, setTitle] = useState('');
  const titleTimer = useRef(null);
  // 디자인 설정(settings) 머지 베이스 — 최신 값을 ref 로 추적(StrictMode 이중 실행 대비).
  const settingsRef = useRef({});

  useEffect(() => {
    let active = true;
    setPageLoading(true);
    setNotFound(false);
    setPage(null);
    if (!pageId) { setPageLoading(false); setNotFound(true); return () => { active = false; }; }
    (async () => {
      // settings/타임스탬프 컬럼은 마이그레이션 전이면 없을 수 있다 → 단계적으로 컬럼을 줄여 재조회(방어적).
      // 핵심 컬럼(id/title/icon/cover)만 성공하면 페이지는 정상 로드되고, 부가 컬럼 부재로 404 가 나지 않게 한다.
      let { data, error } = await supabase
        .schema('workspace').from('pages')
        .select('id, title, icon, cover, settings, updated_at, created_at').eq('id', pageId).maybeSingle();
      if (error) {
        const retry = await supabase
          .schema('workspace').from('pages')
          .select('id, title, icon, cover, settings').eq('id', pageId).maybeSingle();
        data = retry.data; error = retry.error;
      }
      if (error) {
        const retry2 = await supabase
          .schema('workspace').from('pages')
          .select('id, title, icon, cover').eq('id', pageId).maybeSingle();
        data = retry2.data; error = retry2.error;
      }
      if (!active) return;
      if (error || !data) {
        setNotFound(true);
      } else {
        setPage(data);
        setTitle(data.title || '');
        settingsRef.current = data.settings || {};
      }
      setPageLoading(false);
    })();
    return () => { active = false; };
  }, [pageId]);

  const saveTitle = useCallback((nextTitle) => {
    if (!pageId) return;
    if (titleTimer.current) clearTimeout(titleTimer.current);
    titleTimer.current = setTimeout(async () => {
      await supabase.schema('workspace').from('pages').update({ title: nextTitle }).eq('id', pageId);
      window.dispatchEvent(new Event('rt:pages'));
    }, TITLE_DEBOUNCE_MS);
  }, [pageId]);

  useEffect(() => () => { if (titleTimer.current) clearTimeout(titleTimer.current); }, []);

  function onTitleChange(e) {
    const v = e.target.value;
    setTitle(v);
    saveTitle(v);
  }

  // 아이콘/커버 저장 — 즉시 로컬 반영 후 DB update
  const updatePageField = useCallback((field, val) => {
    if (!pageId) return;
    setPage((prev) => (prev ? { ...prev, [field]: val } : prev));
    supabase.schema('workspace').from('pages').update({ [field]: val }).eq('id', pageId)
      .then(() => window.dispatchEvent(new Event('rt:pages')));
  }, [pageId]);

  const onIconChange = useCallback((icon) => updatePageField('icon', icon), [updatePageField]);
  const onCoverChange = useCallback((cover) => updatePageField('cover', cover), [updatePageField]);

  // 디자인 설정(글꼴/작은 텍스트/전체 너비) — pages.settings(JSONB) 머지 저장.
  // 아이콘/커버와 동일 메커니즘(즉시 로컬 반영 후 DB update). settings 컬럼이 없으면 update 는
  // 조용히 실패하지만 로컬 상태는 반영되어 화면은 깨지지 않는다(방어적).
  const updateSettings = useCallback((partial) => {
    if (!pageId) return;
    // 머지 베이스는 ref 로 추적(StrictMode 이중 실행에서도 DB write 1회 보장).
    const next = { ...(settingsRef.current || {}), ...partial };
    settingsRef.current = next;
    setPage((prev) => (prev ? { ...prev, settings: next } : prev));
    supabase.schema('workspace').from('pages').update({ settings: next }).eq('id', pageId)
      .then(() => window.dispatchEvent(new Event('rt:pages')));
  }, [pageId]);

  // 본문 이미지/파일 업로드 — Supabase Storage(workspace-uploads, 공개 버킷)에 올리고 public URL 반환.
  // 이 핸들러가 있으면 BlockNote 이미지/파일 블록에 'URL 임베드' 외에 '업로드' 탭이 활성화된다.
  const uploadFile = useCallback(async (file) => {
    const safeName = (file.name || 'file').replace(/[^\w.\-]+/g, '_');
    const path = `${membership?.org_id || 'org'}/${pageId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeName}`;
    const { error } = await supabase.storage
      .from('workspace-uploads')
      .upload(path, file, { contentType: file.type || undefined, upsert: false });
    if (error) throw error;
    return supabase.storage.from('workspace-uploads').getPublicUrl(path).data.publicUrl;
  }, [pageId, membership?.org_id]);

  const userMeta = {
    name: membership?.display_name || user?.email || '익명',
    color: colorFor(user?.id),
  };
  const collab = useCollab(pageId, userMeta, user?.id);

  if (!pageId || notFound) {
    return (
      <div className="pe-root"><div className="pe-center">
        <div className="pe-big">🔍</div>
        <div className="pe-msg">페이지를 찾을 수 없습니다.</div>
      </div></div>
    );
  }
  if (pageLoading || !page || !collab) {
    return (
      <div className="pe-root"><div className="pe-center">
        <div className="spinner" />
        <div className="muted" style={{ fontSize: 13 }}>문서를 불러오는 중…</div>
      </div></div>
    );
  }

  return (
    <EditorReady
      key={pageId}
      page={page}
      title={title}
      onTitleChange={onTitleChange}
      onIconChange={onIconChange}
      onCoverChange={onCoverChange}
      updateSettings={updateSettings}
      collab={collab}
      userMeta={userMeta}
      uploadFile={uploadFile}
      user={user}
      membership={membership}
    />
  );
}

function EditorReady({ page, title, onTitleChange, onIconChange, onCoverChange, updateSettings, collab, userMeta, uploadFile, user, membership }) {
  const navigate = useNavigate();
  const { theme } = useTheme(); // 실효 테마 ('light'|'dark') — BlockNote theme prop 연동
  const editor = useCreateBlockNote({
    // Phase C — 커스텀 스키마(linkedDatabaseView 포함). 헤드리스 변환기
    // (markdownToYstate.js)와 반드시 동일한 schema 여야 직렬화 호환.
    schema,
    // 멀티 컬럼(xl-multi-column) — 블록 드래그 시 좌/우 가장자리 드롭을 감지해 세로 드롭
    // 커서를 표시(드롭하면 컬럼 생성/편입). DropCursorOptions 의 hooks 슬롯에 그대로 맞는
    // 객체({ hooks })라 기존 드롭커서 설정과 충돌 없음.
    dropCursor: multiColumnDropCursor,
    collaboration: {
      provider: collab.provider,
      fragment: collab.doc.getXmlFragment('document-store'),
      user: { name: userMeta.name, color: userMeta.color },
    },
    uploadFile,
  });

  // 이슈 A — 본문(BlockNoteView) 안 내부 링크(href="/page/<uuid>") 클릭이 항상 새 탭으로
  // 열리는 문제 수정. BlockNote 의 Link 확장은 target="_blank" 를 강제하고, 편집 가능 상태에서는
  // ProseMirror handleClick 플러그인이 자체적으로 window.open 을 호출해 버린다(internalLinkNav.js
  // 상단 주석 참조). 컨테이너에 캡처 단계 클릭 리스너를 달아 BlockNote 가 이벤트를 받기 전에
  // 내부 링크만 가로채 react-router navigate() 로 전환한다. 외부 링크·수정자 클릭은 그대로 새 탭.
  const editorContainerRef = useRef(null);
  useEffect(() => {
    const container = editorContainerRef.current;
    if (!container) return undefined;
    const handleClick = createInternalLinkClickHandler(navigate, () => editorContainerRef.current);
    container.addEventListener('click', handleClick, true);
    return () => container.removeEventListener('click', handleClick, true);
  }, [navigate]);

  const [iconOpen, setIconOpen] = useState(false);
  const [coverOpen, setCoverOpen] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [optionsOpen, setOptionsOpen] = useState(false);
  // Phase 4 — 프레젠테이션 / 애널리틱스 / AI·번역 결과 패널 상태.
  const [presentOpen, setPresentOpen] = useState(false);
  const [analyticsOpen, setAnalyticsOpen] = useState(false);
  // AI/번역 결과 드로어 — { mode, title, loading, text, error, lang? } | null
  const [aiPanel, setAiPanel] = useState(null);
  const hasCover = !!page.cover;

  // 디자인 설정 — settings 컬럼이 없으면 {} 기본값(방어적).
  const settings = page.settings || {};
  const locked = !!settings.locked;

  // ── 페이지 액션 ─────────────────────────────────────────────────────────
  // 옮기기 대상 목록 — 같은 org 의 document 타입 페이지(자기 자신 제외).
  // 순환(자기 하위로 이동) 방지를 위해 자기 자신 및 자기 하위 트리는 제외한다.
  const [moveTargets, setMoveTargets] = useState([]);
  useEffect(() => {
    let active = true;
    (async () => {
      const orgId = membership?.org_id;
      if (!orgId) return;
      const { data } = await supabase
        .schema('workspace').from('pages')
        .select('id, title, icon, parent_id')
        .eq('org_id', orgId).eq('archived', false).eq('type', 'document')
        .order('position', { ascending: true });
      if (!active || !data) return;
      // 자기 자신 + 자기 하위 트리(순환) 제외
      const byParent = new Map();
      data.forEach((p) => {
        const arr = byParent.get(p.parent_id) || [];
        arr.push(p);
        byParent.set(p.parent_id, arr);
      });
      const blocked = new Set([page.id]);
      const stack = [page.id];
      while (stack.length) {
        const cur = stack.pop();
        (byParent.get(cur) || []).forEach((c) => {
          if (!blocked.has(c.id)) { blocked.add(c.id); stack.push(c.id); }
        });
      }
      setMoveTargets(data.filter((p) => !blocked.has(p.id)));
    })();
    return () => { active = false; };
  }, [membership?.org_id, page.id]);

  // 애널리틱스용 조회 기록 — 페이지(이 컴포넌트는 pageId 로 keying) 마운트 시 1회 insert.
  // page_views 테이블이 아직 없으면(마이그레이션 전) try/catch 로 조용히 무시(방어적).
  const viewLoggedRef = useRef(false);
  useEffect(() => {
    if (viewLoggedRef.current) return; // StrictMode 이중 마운트에서도 1회만.
    if (!page?.id || !user?.id) return;
    viewLoggedRef.current = true;
    (async () => {
      try {
        await supabase
          .schema('workspace').from('page_views')
          .insert({ page_id: page.id, user_id: user.id, viewed_at: new Date().toISOString() });
      } catch (e) {
        // 테이블 없음/RLS — 조회 기록은 부가 기능이므로 무시.
      }
    })();
  }, [page?.id, user?.id]);

  // 1) 링크 복사 — 현재 URL 클립보드 복사(성공 시 true 반환 → 메뉴에서 "복사됨" 피드백).
  const onCopyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  // 2) 복제 — 현재 페이지를 같은 부모 아래로 복사(본문 ystate 포함). source_ref 는 복사하지 않음(멱등 매칭 오염 방지).
  const onDuplicate = useCallback(async () => {
    const orgId = membership?.org_id;
    if (!orgId || !user?.id) return;
    const row = {
      org_id: orgId,
      parent_id: page.parent_id ?? null,
      teamspace_id: page.teamspace_id ?? null,
      type: page.type || 'document',
      title: `${title || '제목 없음'} (사본)`,
      icon: page.icon || null,
      settings: page.settings || {},
      position: Date.now(),
      created_by: user.id,
      source_ref: null,
    };
    const { data: created, error } = await supabase
      .schema('workspace').from('pages').insert(row).select('id').single();
    if (error || !created) {
      // eslint-disable-next-line no-console
      console.error('[루트/editor] 페이지 복제 실패', error);
      return;
    }
    // 본문(page_content.ystate) 복사 — 있을 때만.
    const { data: src } = await supabase
      .schema('workspace').from('page_content')
      .select('ystate').eq('page_id', page.id).maybeSingle();
    if (src && src.ystate) {
      await supabase.schema('workspace').from('page_content')
        .insert({ page_id: created.id, ystate: src.ystate, updated_at: new Date().toISOString() });
    }
    window.dispatchEvent(new Event('rt:pages'));
    navigate(`/page/${created.id}`);
  }, [membership?.org_id, user?.id, page, title, navigate]);

  // 3) 옮기기 — 부모 변경(다른 페이지 하위 또는 최상위). 순환은 moveTargets 단계에서 이미 차단.
  const onMove = useCallback(async (parentId) => {
    const { error } = await supabase
      .schema('workspace').from('pages')
      .update({ parent_id: parentId }).eq('id', page.id);
    if (error) {
      // eslint-disable-next-line no-console
      console.error('[루트/editor] 페이지 옮기기 실패', error);
      return;
    }
    window.dispatchEvent(new Event('rt:pages'));
  }, [page.id]);

  // 4) 페이지 잠금 토글 — settings.locked. 잠금 시 에디터/제목 읽기 전용.
  const onToggleLock = useCallback((next) => {
    updateSettings({ locked: !!next });
  }, [updateSettings]);

  // 5) 내보내기(Markdown) — BlockNote 헤드리스 변환 후 .md 다운로드.
  const onExport = useCallback(async () => {
    try {
      const md = await editor.blocksToMarkdownLossy(editor.document);
      const safeName = (title || '제목 없음').replace(/[\\/:*?"<>|]+/g, '_');
      const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safeName}.md`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[루트/editor] 마크다운 내보내기 실패', e);
    }
  }, [editor, title]);

  // ── Phase 4: AI 보조 / 번역 ──────────────────────────────────────────────
  // 공통 호출 — 본문 텍스트를 추출해 ai-assist edge function 으로 보낸다.
  // 계약: invoke('ai-assist', { body: { action, text, targetLang? } }) → { ok, result } | { ok:false, error }.
  // 키 미설정 등으로 ok:false 면 안내 문구를 패널에 표시.
  const callAiAssist = useCallback(async ({ mode, panelTitle, action, targetLang }) => {
    setAnalyticsOpen(false);
    setCommentsOpen(false);
    setVersionsOpen(false);
    setOptionsOpen(false);
    setAiPanel({ mode, title: panelTitle, loading: true, text: '', error: '', lang: targetLang || null });
    let bodyText = '';
    try {
      bodyText = await editor.blocksToMarkdownLossy(editor.document);
    } catch (e) {
      bodyText = '';
    }
    if (!bodyText.trim()) {
      setAiPanel({ mode, title: panelTitle, loading: false, text: '', error: '본문이 비어 있어요. 내용을 입력한 뒤 다시 시도해주세요.', lang: targetLang || null });
      return;
    }
    try {
      const body = { action, text: bodyText };
      if (targetLang) body.targetLang = targetLang;
      const { data, error } = await supabase.functions.invoke('ai-assist', { body });
      if (error) {
        // 함수 미배포/네트워크 등 — invoke 자체 에러.
        setAiPanel({ mode, title: panelTitle, loading: false, text: '', error: 'AI 기능을 호출하지 못했어요. 잠시 후 다시 시도해주세요.', lang: targetLang || null });
        return;
      }
      if (!data || data.ok === false) {
        // edge function 이 ok:false (키 미설정 등) 반환.
        const raw = (data && data.error) || '';
        const friendly = /key|ANTHROPIC|api/i.test(String(raw))
          ? 'AI 사용에는 ANTHROPIC_API_KEY 설정이 필요합니다.'
          : (raw || 'AI 처리에 실패했어요.');
        setAiPanel({ mode, title: panelTitle, loading: false, text: '', error: friendly, lang: targetLang || null });
        return;
      }
      setAiPanel({ mode, title: panelTitle, loading: false, text: String(data.result || ''), error: '', lang: targetLang || null });
    } catch (e) {
      setAiPanel({ mode, title: panelTitle, loading: false, text: '', error: 'AI 기능을 호출하지 못했어요. 잠시 후 다시 시도해주세요.', lang: targetLang || null });
    }
  }, [editor]);

  const onAiAction = useCallback((action) => {
    const map = {
      summarize: '요약',
      continue: '이어쓰기',
      improve: '문장 다듬기',
    };
    callAiAssist({ mode: 'ai', panelTitle: `AI · ${map[action] || action}`, action });
  }, [callAiAssist]);

  const onTranslate = useCallback((targetLang) => {
    const langName = { en: '영어', ja: '일본어', zh: '중국어', ko: '한국어' }[targetLang] || targetLang;
    callAiAssist({ mode: 'translate', panelTitle: `번역 · ${langName}`, action: 'translate', targetLang });
  }, [callAiAssist]);

  // AI/번역 결과를 본문 마지막에 삽입 — 마크다운을 블록으로 파싱해 문서 끝에 append.
  const onInsertAiResult = useCallback(async (text) => {
    if (!text || !text.trim()) return;
    try {
      // 마크다운 → BlockNote 블록(가능하면). 실패 시 단락 1개로 폴백.
      let blocks;
      if (typeof editor.tryParseMarkdownToBlocks === 'function') {
        blocks = await editor.tryParseMarkdownToBlocks(text);
      }
      if (!blocks || !blocks.length) {
        blocks = [{ type: 'paragraph', content: text }];
      }
      const doc = editor.document;
      const last = doc[doc.length - 1];
      if (last) {
        editor.insertBlocks(blocks, last, 'after');
      } else {
        editor.replaceBlocks(editor.document, blocks);
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[루트/editor] AI 결과 본문 삽입 실패', e);
    }
  }, [editor]);

  // 6) 버전 복원 — 선택한 버전의 ystate 로 page_content 를 되돌린다.
  //    (1) 복원 전 현재 상태를 버전으로 백업(되돌리기 가능) → (2) page_content upsert →
  //    (3) 로컬 IndexedDB(root-page-<id>) 삭제(stale Yjs 캐시 제거) → (4) reload.
  const onRestoreVersion = useCallback(async (version) => {
    if (!version || !version.ystate) throw new Error('빈 버전');
    // (1) 현재 본문을 버전으로 남겨 복원 자체도 되돌릴 수 있게.
    const { data: cur } = await supabase
      .schema('workspace').from('page_content')
      .select('ystate').eq('page_id', page.id).maybeSingle();
    if (cur && cur.ystate) {
      try {
        await supabase.schema('workspace').from('page_versions')
          .insert({ page_id: page.id, ystate: cur.ystate, created_by: user?.id || null, label: '복원 직전' });
      } catch (e) { /* 백업 실패해도 복원은 진행(best-effort) */ }
    }
    // (2) 선택 버전으로 본문 교체.
    const { error } = await supabase
      .schema('workspace').from('page_content')
      .upsert(
        { page_id: page.id, ystate: version.ystate, updated_at: new Date().toISOString() },
        { onConflict: 'page_id' }
      );
    if (error) throw error;
    // (3) 로컬 Yjs 캐시 삭제 — 안 지우면 stale IndexedDB 가 다시 머지돼 복원이 무효화됨.
    try {
      if (typeof indexedDB !== 'undefined' && indexedDB.deleteDatabase) {
        indexedDB.deleteDatabase(`root-page-${page.id}`);
      }
    } catch (e) { /* noop */ }
    // (4) 새 ystate 로 로드.
    window.location.reload();
  }, [page.id, user?.id]);

  const docClass = [
    'pe-doc',
    hasCover ? 'has-cover' : '',
    settings.font === 'serif' ? 'pe-font-serif' : '',
    settings.font === 'mono' ? 'pe-font-mono' : '',
    settings.smallText ? 'pe-small-text' : '',
    settings.fullWidth ? 'pe-full-width' : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={`pe-root${commentsOpen ? ' pe-has-comments' : ''}`}>
      <div className="pe-topbar">
        <span className="pe-crumb">
          {/* 노션 동기화: 아이콘 없으면 옅은 기본 문서 아이콘(강제 📄 표시 안 함) */}
          <span className={page.icon ? undefined : 'pe-crumb-faint'}>{page.icon || '📄'}</span>
          {' '}{title || '제목 없음'}
        </span>
        {/* 노션식 우측 액션 그룹 — crumb 다음에 margin-left:auto 로 우측 끝 정렬 */}
        <div className="pe-topbar-actions">
          <Presence awareness={collab.awareness} />
          <button
            type="button"
            className={`pe-comment-toggle${commentsOpen ? ' is-active' : ''}`}
            onClick={() => setCommentsOpen((o) => { if (!o) setVersionsOpen(false); return !o; })}
            aria-label="댓글"
            title="댓글"
          >
            💬
          </button>
          <button
            type="button"
            className="pe-share-btn"
            onClick={() => setShareOpen(true)}
            aria-label="공유"
            title="공유"
          >
            공유
          </button>
          <div className="pe-options-wrap">
            <button
              type="button"
              className="pe-options-btn"
              onClick={() => setOptionsOpen((o) => !o)}
              aria-label="옵션"
              title="옵션"
            >
              ⋯
            </button>
            {optionsOpen ? (
              <PageOptionsMenu
                settings={settings}
                onChange={updateSettings}
                onClose={() => setOptionsOpen(false)}
                onCopyLink={onCopyLink}
                onDuplicate={onDuplicate}
                onMove={onMove}
                onToggleLock={onToggleLock}
                onExport={onExport}
                onVersionHistory={() => { setCommentsOpen(false); setVersionsOpen(true); }}
                onPresent={() => { setOptionsOpen(false); setPresentOpen(true); }}
                onAnalytics={() => { setCommentsOpen(false); setVersionsOpen(false); setAiPanel(null); setAnalyticsOpen(true); }}
                onAiAction={onAiAction}
                onTranslate={onTranslate}
                moveTargets={moveTargets}
              />
            ) : null}
          </div>
        </div>
      </div>
      {shareOpen ? <ShareModal pageId={page.id} onClose={() => setShareOpen(false)} /> : null}

      {hasCover ? (
        <div className="pe-cover" style={{ background: page.cover }}>
          <div className="pe-cover-actions">
            <div className="pe-cover-wrap">
              <button type="button" className="pe-cover-btn" onClick={() => setCoverOpen((o) => !o)}>
                커버 변경
              </button>
              {coverOpen ? (
                <CoverPicker
                  value={page.cover}
                  onSelect={(c) => { onCoverChange(c); setCoverOpen(false); }}
                  onClose={() => setCoverOpen(false)}
                />
              ) : null}
            </div>
            <button type="button" className="pe-cover-btn" onClick={() => onCoverChange(null)}>
              제거
            </button>
          </div>
        </div>
      ) : null}

      <div className={docClass}>
        {!hasCover ? (
          <div className="pe-doc-tools">
            <div className="pe-cover-wrap">
              <button type="button" className="pe-tool-btn" onClick={() => setCoverOpen((o) => !o)}>
                🖼️ 커버 추가
              </button>
              {coverOpen ? (
                <CoverPicker
                  value={page.cover}
                  onSelect={(c) => { onCoverChange(c); setCoverOpen(false); }}
                  onClose={() => setCoverOpen(false)}
                />
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="pe-icon-wrap">
          <button
            type="button"
            className={`pe-icon${page.icon ? '' : ' pe-icon-faint'}`}
            aria-label="페이지 아이콘 변경"
            onClick={() => setIconOpen((o) => !o)}
          >
            {/* 노션 동기화: 아이콘 없으면 옅은 기본 문서 아이콘(클릭 시 아이콘 설정) */}
            {page.icon || '📄'}
          </button>
          {iconOpen ? (
            <IconPicker
              value={page.icon || null}
              onSelect={(i) => { onIconChange(i); setIconOpen(false); }}
              onClose={() => setIconOpen(false)}
            />
          ) : null}
        </div>

        <input
          className="pe-title"
          value={title}
          onChange={onTitleChange}
          placeholder="제목 없음"
          spellCheck={false}
          aria-label="페이지 제목"
          readOnly={locked}
        />
        <div className="pe-editor" ref={editorContainerRef}>
          {/* 기본 사이드 메뉴(거의 빈 드래그핸들 메뉴)를 끄고, 노션식 커스텀 메뉴로 교체.
              BlockSideMenu 는 BlockNoteView 의 자식으로 렌더돼야 editor 컨텍스트를 받는다. */}
          {/* slashMenu={false}: 기본 슬래시 메뉴를 끄고 SuggestionMenuController 로 교체 —
              기본 항목 + 커스텀 블록(수식/북마크/동기화 블록/임베드/콜아웃) 삽입 항목 제공. */}
          <BlockNoteView editor={editor} theme={theme} editable={!locked} sideMenu={false} slashMenu={false}>
            <BlockSideMenu />
            <SuggestionMenuController
              triggerCharacter="/"
              getItems={async (query) => getRootSlashMenuItems(editor, query)}
            />
          </BlockNoteView>
        </div>
      </div>

      {commentsOpen ? (
        <>
          <div
            className="pe-comments-backdrop"
            onClick={() => setCommentsOpen(false)}
            aria-hidden="true"
          />
          <div className="pe-comments-drawer">
            <CommentsPanel pageId={page.id} onClose={() => setCommentsOpen(false)} />
          </div>
        </>
      ) : null}

      {versionsOpen ? (
        <>
          <div
            className="pe-comments-backdrop"
            onClick={() => setVersionsOpen(false)}
            aria-hidden="true"
          />
          <div className="pe-comments-drawer">
            <VersionHistory
              pageId={page.id}
              onClose={() => setVersionsOpen(false)}
              onRestore={onRestoreVersion}
            />
          </div>
        </>
      ) : null}

      {/* Phase 4: 업데이트와 애널리틱스 드로어 */}
      {analyticsOpen ? (
        <>
          <div
            className="pe-comments-backdrop"
            onClick={() => setAnalyticsOpen(false)}
            aria-hidden="true"
          />
          <div className="pe-comments-drawer">
            <AnalyticsPanel page={page} onClose={() => setAnalyticsOpen(false)} />
          </div>
        </>
      ) : null}

      {/* Phase 4: AI / 번역 결과 드로어 */}
      {aiPanel ? (
        <>
          <div
            className="pe-comments-backdrop"
            onClick={() => setAiPanel(null)}
            aria-hidden="true"
          />
          <div className="pe-comments-drawer">
            <aside className="cm-panel" role="complementary" aria-label={aiPanel.title}>
              <header className="cm-head">
                <span className="cm-head-title">{aiPanel.title}</span>
                <button type="button" className="cm-close" onClick={() => setAiPanel(null)} aria-label="닫기">✕</button>
              </header>
              <div className="cm-list">
                {aiPanel.loading ? (
                  <div className="cm-state">
                    <div className="spinner" />
                    <div className="muted" style={{ fontSize: 13 }}>AI가 처리하는 중…</div>
                  </div>
                ) : aiPanel.error ? (
                  <div className="cm-state cm-state-err">{aiPanel.error}</div>
                ) : (
                  <div className="ai-result">{aiPanel.text}</div>
                )}
              </div>
              {!aiPanel.loading && !aiPanel.error && aiPanel.text ? (
                <div className="ai-result-foot">
                  <button
                    type="button"
                    className="cm-submit"
                    onClick={() => { onInsertAiResult(aiPanel.text); setAiPanel(null); }}
                    disabled={locked}
                    title={locked ? '잠긴 페이지에는 삽입할 수 없어요' : undefined}
                  >
                    본문에 삽입
                  </button>
                </div>
              ) : null}
            </aside>
          </div>
        </>
      ) : null}

      {/* Phase 4: 프레젠테이션 모드(전체화면 오버레이) */}
      {presentOpen ? (
        <PresentationView
          blocks={editor.document}
          title={title}
          onClose={() => setPresentOpen(false)}
        />
      ) : null}
    </div>
  );
}
